import React, {useContext} from "react";
import Grid from "@mui/material/GridLegacy";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {Controller, useForm} from "react-hook-form";
import {CircularProgress, FormControl} from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import {IState} from "../../queries/pharmacy/pharmacyModel";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import Box from "@mui/material/Box";
import {pharmacyDetailsValidationSchema} from "../../constants/validationSchemas";
import { pageTitleSx } from "../../constants/designTokens";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {useMutationUpdatePharmacy} from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import {NotificationType} from "../../constants/global";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {useQueryFindPharmacy} from "../../queries/pharmacy/hooks/useQueryFindPharmacy";

interface PharmacyDetailsProps {
  data: any;
  states: IState[];
  pharmacyId: number;
  isAdminEdit?: boolean;
  refetchPharmacy?: () => void;
}

const PharmacyDetails:React.FunctionComponent<PharmacyDetailsProps> = ({
  data,
  states,
  pharmacyId,
  isAdminEdit,
  refetchPharmacy
}) => {
  const {addToast} = useContext(ToasterContext);

  const [findPharmacy] = useQueryFindPharmacy({
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache'
  });

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();

  const { register, handleSubmit, formState: { errors }, control } = useForm({
    resolver: yupResolver(pharmacyDetailsValidationSchema(findPharmacy, pharmacyId))
  });

  const handlePharmacyDetailsSubmit = async (values: any) => {
    const requestData = {
      deaNumber: { set: values.deaNumber },
      email: { set: values.email },
      fax: { set: values.fax},
      federalTaxId: { set: values.federalTaxId},
      legalName: { set: values.legalName },
      mobile: { set: values.mobile },
      ncpdp: { set: values.ncpdp },
      npi: { set: values.npi },
      phone: { set: values.phone },
      site: { set: values.site },
      softwareName: { set: values.softwareName },
      store: { set: +values.store  },
      PharmacyLicensedStates: {
        update: {
          State: {
            connect: {
              id: +values.stateLicense
            }
          }
        }
      },
    }

    updatePharmacy({ variables: { data: requestData, where: { id: pharmacyId } } }).then(() => {
      addToast({ type: NotificationType.Success, title: 'Your changes has been successfully saved'  });
      refetchPharmacy && refetchPharmacy();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }

  return (
    <Box component="form" onSubmit={handleSubmit(handlePharmacyDetailsSubmit)} noValidate>
      { !isAdminEdit && <Typography component="h1" sx={{ ...pageTitleSx, mb: 3 }}>Pharmacy Details</Typography> }
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Legal Name"
            id="legalName"
            fullWidth
            defaultValue={data.legalName}
            required
            helperText={errors.legalName?.message?.toString()}
            {...register('legalName')}
            error={!!errors.legalName}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="ncpdp"
            label="Pharmacy NCPDP"
            id="ncpdp"
            defaultValue={data.ncpdp}
            fullWidth
            required
            helperText={errors.ncpdp?.message?.toString()}
            {...register('ncpdp')}
            error={!!errors.ncpdp}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="npi"
            label="Pharmacy NPI"
            id="npi"
            fullWidth
            defaultValue={data.npi}
            required
            helperText={errors.npi?.message?.toString()}
            {...register('npi')}
            error={!!errors.npi}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="deaNumber"
            label="DEA Number"
            id="deaNumber"
            defaultValue={data.deaNumber}
            fullWidth
            required
            helperText={errors.deaNumber?.message?.toString()}
            {...register('deaNumber')}
            error={!!errors.deaNumber}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="verifyDeaNumber"
            label="Verify DEA Number"
            id="verifyDeaNumber"
            fullWidth
            defaultValue={data.deaNumber}
            required
            helperText={errors.verifyDeaNumber?.message?.toString()}
            {...register('verifyDeaNumber')}
            error={!!errors.verifyDeaNumber}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Controller
            name="stateLicense"
            control={control}
            defaultValue={data.PharmacyLicensedStates.State.id}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="stateLicense-label">Pharmacy License</InputLabel>
                <Select
                  {...field}
                  labelId="stateLicense"
                  label="Pharmacy License"
                  id="stateLicense"
                  fullWidth
                  required
                  {...register('stateLicense')}
                  error={!!errors.stateLicense}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  {states.map((state: IState) => (
                    <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="federalTaxId"
            label="Federal Tax ID"
            id="federalTaxId"
            defaultValue={data.federalTaxId}
            fullWidth
            required
            helperText={errors.federalTaxId?.message?.toString()}
            {...register('federalTaxId')}
            error={!!errors.federalTaxId}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Software Name"
            id="softwareName"
            fullWidth
            required
            defaultValue={data.softwareName}
            helperText={errors.softwareName?.message?.toString()}
            {...register('softwareName')}
            error={!!errors.softwareName}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="email"
            label="Email Address"
            id="email"
            fullWidth
            defaultValue={data.email}
            required
            {...register('email')}
            helperText={errors.email?.message?.toString()}
            error={!!errors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="phone"
            label="Phone"
            id="phone"
            fullWidth
            defaultValue={data.phone}
            required
            {...register('phone')}
            helperText={errors.phone?.message?.toString()}
            error={!!errors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="mobile"
            label="Mobile"
            id="mobile"
            defaultValue={data.mobile}
            fullWidth
            required
            {...register('mobile')}
            helperText={errors.mobile?.message?.toString()}
            error={!!errors.mobile}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="fax"
            label="Fax"
            id="fax"
            fullWidth
            defaultValue={data.fax}
            required
            {...register('fax')}
            helperText={errors.fax?.message?.toString()}
            error={!!errors.fax}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="site"
            label="Website"
            id="site"
            fullWidth
            defaultValue={data.site}
            {...register('site')}
            helperText={errors.site?.message?.toString()}
            error={!!errors.site}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            autoComplete="store"
            label="Number of Stores"
            id="store"
            fullWidth
            defaultValue={data.store}
            required
            {...register('store')}
            helperText={errors.store?.message?.toString()}
            error={!!errors.store}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', pt: 2 }}>
        <Button type="submit" variant='contained' sx={{width: '200px'}}>
          Save
          { loading && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
        </Button>
      </Box>
    </Box>
  )
}

export default PharmacyDetails;